import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { useState, useCallback } from 'react';
import { ApiResponse, ApiError, RequestConfig, ApiSuccessResponse, ApiErrorResponse } from './api.types';
import { CustomMapping } from '../types/mapping';
import { authService } from './auth.service';
import { Template } from '../pages/dashboard/components/MappingDialogs';



type CustomerMappingResponse<T> =
  | ApiSuccessResponse<T>
  | ApiErrorResponse;

const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 300000,
  });

  // Request interceptor
  instance.interceptors.request.use(
    (config) => {
      const token = authService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        // Handle unauthorized error (e.g., redirect to login)
        authService.logout();
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

export const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const api = createAxiosInstance();

  // GET request
  const get = useCallback(async <T>(
    url: string,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(url, config);
      return {
        data: response.data,
        status: response.status,
      };
    } catch (err: any) {
      const error: ApiError = {
        message: err.message,
        status: err.response?.status || 500,
        errors: err.response?.data,
      };
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // POST request
  const post = useCallback(async <T>(
    url: string,
    data: any,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.post(url, data, config);
      return {
        data: response.data,
        status: response.status,
      };
    } catch (err: any) {
      const error: ApiError = {
        message: err.message,
        status: err.response?.status || 500,
        errors: err.response?.data,
      };
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // PUT request
  const put = useCallback(async <T>(
    url: string,
    data: any,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.put(url, data, config);
      return {
        data: response.data,
        status: response.status,
      };
    } catch (err: any) {
      const error: ApiError = {
        message: err.message,
        status: err.response?.status || 500,
        errors: err.response?.data,
      };
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // DELETE request
  const del = useCallback(async <T>(
    url: string,
    config?: RequestConfig
  ): Promise<ApiResponse<T>> => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.delete(url, config);
      return {
        data: response.data,
        status: response.status,
      };
    } catch (err: any) {
      const error: ApiError = {
        message: err.message,
        status: err.response?.status || 500,
        errors: err.response?.data,
      };
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getFieldMapping = () => {
    return get<ApiSuccessResponse<CustomMapping[]>>('/field-mapping');
};

const createFieldMapping = (mapping: CustomMapping) => {
    return post<ApiSuccessResponse<CustomMapping>>('/field-mapping', mapping);
};

const updateFieldMapping = (id: string, mapping: CustomMapping) => {
    return put<ApiSuccessResponse<CustomMapping>>(`/field-mapping/${id}`, mapping);
};

const deleteFieldMapping = (id: string) => {
    return del<ApiSuccessResponse<void>>(`/field-mapping/${id}`);
};

const getFieldMappingById = (id: string) => {
    return get<ApiSuccessResponse<CustomMapping>>(`/field-mapping/${id}`);
};

const getTemplates = (type: string) => {
    return get<ApiSuccessResponse<Template[]>>('/template', { params: { type } });
};

const createTemplate = (template: Template) => {
    return post<ApiSuccessResponse<Template>>('/template', template);
};

const updateTemplate = (id: string, template: Template) => {
    return put<ApiSuccessResponse<Template>>(`/template/${id}`, template);
};

const deleteTemplate = (id: string) => {
    return del<ApiSuccessResponse<void>>(`/template/${id}`);
};

  return {
    get,
    post,
    put,
    del,
    loading,
    error,
    getFieldMapping,
    createFieldMapping,
    updateFieldMapping,
    deleteFieldMapping,
    getFieldMappingById,
    getTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  };
}; 